import type { ShopProductAssignBody } from "@/modules/shops/domain/contracts";
import type { ShopFeatureFlags } from "@/modules/shops/domain/contracts/shop-feature-flags";
import type { ShopsListResult } from "@/modules/shops/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type ShopItem = ShopsListResult["data"][number];

export type ShopsStats = {
  total: number;
  active: number;
  verified: number;
  pendingApproval: number;
};

export type BranchItem = {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  isActive: boolean;
  /** null = ຕາມສະກຸນຮ້ານ */
  defaultCurrency: string | null;
};

export type ShopDetail = {
  id: string;
  name: string;
  status: string;
  verifiedLevel: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  taxId: string | null;
  defaultTaxPercent: number;
  defaultCurrency: string;
  enforceSingleShopSkuCurrency: boolean;
  featureFlags: ShopFeatureFlags;
  createdAt: string;
  updatedAt: string;
  branches: BranchItem[];
  staff: Array<{
    id: string;
    userId: string;
    userName: string | null;
    userEmail: string;
    staffRole: string;
    branchId: string | null;
  }>;
};

export type BranchesResponse = {
  branches: BranchItem[];
  shopName: string;
};

export type Period = "day" | "week" | "month";

/** C2. รายการสินค้าของร้าน (ต่อ shop_product_sku) */
export type ShopProductSkuListItem = {
  id: string;
  shopId: string;
  shopProductId: string;
  catalogProductId: string;
  catalogSkuId: string;
  productName: string;
  oemPartNo: string | null;
  fitmentStatus: "pending" | "complete";
  thumbnailUrl: string | null;
  sku: string;
  barcode: string | null;
  price: number;
  cost: number | null;
  currency: string;
  isActive: boolean;
  productStatus: string;
  updatedAt: string;
};

export type ShopProductListResult = {
  data: ShopProductSkuListItem[];
  meta: { total: number; limit: number; offset: number };
};

export type ShopPerformance = {
  totalSales: number;
  totalSalesChangePercent: number;
  totalOrders: number;
  totalOrdersChangePercent: number;
  productsSold: number;
  productsSoldChangePercent: number;
  activeBranches: number;
  activeBranchesLabel: string;
  salesTrends: Array<{ day: string; current: number; previous: number }>;
  branchComparison: Array<{ name: string; volume: number }>;
};

export type CreateShopInput = {
  name: string;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  taxId?: string | null;
  defaultTaxPercent?: number;
  defaultCurrency?: "LAK" | "THB" | "USD";
  enforceSingleShopSkuCurrency?: boolean;
};

export type UpdateShopInput = {
  name?: string;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  taxId?: string | null;
  defaultTaxPercent?: number;
  defaultCurrency?: "LAK" | "THB" | "USD";
  enforceSingleShopSkuCurrency?: boolean;
};

/** สร้าง user ใหม่แล้วเพิ่มเป็น staff ในร้าน (พร้อม preset permissions ตาม staffRole) */
export type CreateStaffInput = {
  email: string;
  name: string;
  password: string;
  staffRole: "owner" | "manager" | "cashier";
  branchId?: string | null;
};

export type AddStaffInput = {
  userId: string;
  staffRole: "owner" | "manager" | "cashier";
  branchId?: string | null;
};

export type UpdateStaffInput = {
  email: string;
  name: string;
  password?: string;
};

export type CreateBranchInput = {
  name: string;
  phone?: string | null;
  address?: string | null;
  isActive?: boolean;
  defaultCurrency?: "LAK" | "THB" | "USD" | null;
};

export type UpdateBranchInput = {
  name?: string;
  phone?: string | null;
  address?: string | null;
  isActive?: boolean;
  defaultCurrency?: "LAK" | "THB" | "USD" | null;
};

export const shopsApi = {
  async create(input: CreateShopInput): Promise<ShopDetail> {
    return fetcher.post<ShopDetail>(`${config.apiUrl}/shops`, input);
  },
  async getById(id: string): Promise<ShopDetail> {
    return fetcher.get<ShopDetail>(`${config.apiUrl}/shops/${id}`);
  },
  async update(id: string, input: UpdateShopInput): Promise<ShopDetail> {
    return fetcher.put<ShopDetail>(`${config.apiUrl}/shops/${id}`, input);
  },
  async patchFeatureFlags(
    id: string,
    input: Partial<ShopFeatureFlags>,
  ): Promise<ShopDetail> {
    return fetcher.patch<ShopDetail>(
      `${config.apiUrl}/shops/${id}/feature-flags`,
      input,
    );
  },
  async suspend(id: string): Promise<ShopDetail> {
    return fetcher.post<ShopDetail>(`${config.apiUrl}/shops/${id}/suspend`);
  },
  async unsuspend(id: string): Promise<ShopDetail> {
    return fetcher.post<ShopDetail>(`${config.apiUrl}/shops/${id}/unsuspend`);
  },
  /** สร้าง user ใหม่และเพิ่มเป็น staff ในร้าน (preset shop_roles ตาม staffRole) */
  async addStaff(id: string, input: CreateStaffInput): Promise<ShopDetail> {
    return fetcher.post<ShopDetail>(
      `${config.apiUrl}/shops/${id}/staff`,
      input,
    );
  },
  async updateStaff(
    id: string,
    staffId: string,
    input: UpdateStaffInput,
  ): Promise<ShopDetail> {
    return fetcher.put<ShopDetail>(
      `${config.apiUrl}/shops/${id}/staff/${staffId}`,
      input,
    );
  },
  async verify(id: string, level: "basic" | "verified"): Promise<ShopDetail> {
    return fetcher.post<ShopDetail>(`${config.apiUrl}/shops/${id}/verify`, {
      level,
    });
  },
  async stats(): Promise<ShopsStats> {
    return fetcher.get<ShopsStats>(`${config.apiUrl}/shops/stats`);
  },
  async getPerformance(period: Period = "week"): Promise<ShopPerformance> {
    return fetcher.get<ShopPerformance>(
      `${config.apiUrl}/shops/performance?period=${period}`,
    );
  },

  async getBranches(shopId: string): Promise<BranchesResponse> {
    return fetcher.get<BranchesResponse>(
      `${config.apiUrl}/shops/${shopId}/branches`,
    );
  },

  async createBranch(
    shopId: string,
    input: CreateBranchInput,
  ): Promise<BranchItem> {
    return fetcher.post<BranchItem>(
      `${config.apiUrl}/shops/${shopId}/branches`,
      input,
    );
  },

  async updateBranch(
    shopId: string,
    branchId: string,
    input: UpdateBranchInput,
  ): Promise<BranchItem> {
    return fetcher.put<BranchItem>(
      `${config.apiUrl}/shops/${shopId}/branches/${branchId}`,
      input,
    );
  },

  async setBranchActive(
    shopId: string,
    branchId: string,
    isActive: boolean,
  ): Promise<BranchItem> {
    return fetcher.patch<BranchItem>(
      `${config.apiUrl}/shops/${shopId}/branches/${branchId}`,
      { isActive },
    );
  },

  async deleteBranch(
    shopId: string,
    branchId: string,
  ): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${config.apiUrl}/shops/${shopId}/branches/${branchId}`,
    );
  },

  async list(query: OffsetPageQueryDTO): Promise<ShopsListResult> {
    const url = new URL(`${config.apiUrl}/shops`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ShopsListResult>(url.toString());
  },

  // ----- C2. Shop Products (ສິນຄ້າຂອງຮ້ານ) -----
  async listShopProducts(
    shopId: string,
    params: {
      limit?: number;
      offset?: number;
      status?: string;
      search?: string;
    },
  ): Promise<ShopProductListResult> {
    const url = new URL(`${config.apiUrl}/shops/${shopId}/products`);
    if (params.limit != null)
      url.searchParams.set("limit", String(params.limit));
    if (params.offset != null)
      url.searchParams.set("offset", String(params.offset));
    if (params.status) url.searchParams.set("status", params.status);
    if (params.search?.trim())
      url.searchParams.set("search", params.search.trim());
    return fetcher.get<ShopProductListResult>(url.toString());
  },

  async assignProduct(
    shopId: string,
    body: ShopProductAssignBody,
  ): Promise<{ shopProductId: string }> {
    return fetcher.post<{ shopProductId: string }>(
      `${config.apiUrl}/shops/${shopId}/products`,
      body,
    );
  },

  async getShopProductSku(
    shopId: string,
    shopProductSkuId: string,
  ): Promise<ShopProductSkuListItem> {
    return fetcher.get<ShopProductSkuListItem>(
      `${config.apiUrl}/shops/${shopId}/skus/${shopProductSkuId}`,
    );
  },

  async updateShopProductSku(
    shopId: string,
    shopProductSkuId: string,
    body: {
      sku?: string;
      barcode?: string | null;
      price?: number;
      cost?: number | null;
      currency?: "LAK" | "THB" | "USD";
      isActive?: boolean;
    },
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${config.apiUrl}/shops/${shopId}/skus/${shopProductSkuId}`,
      body,
    );
  },

  async removeShopProductSku(
    shopId: string,
    shopProductSkuId: string,
    mode: "soft" | "hard" = "hard",
  ): Promise<{ ok: boolean; soft?: boolean; mode?: string }> {
    const url = new URL(
      `${config.apiUrl}/shops/${shopId}/skus/${shopProductSkuId}`,
    );
    url.searchParams.set("mode", mode);
    return fetcher.delete<{ ok: boolean; soft?: boolean; mode?: string }>(
      url.toString(),
    );
  },

  async unassignShopProduct(
    shopId: string,
    shopProductId: string,
  ): Promise<{ ok: boolean; soft?: boolean }> {
    return fetcher.delete<{ ok: boolean; soft?: boolean }>(
      `${config.apiUrl}/shops/${shopId}/products/${shopProductId}`,
    );
  },
};
